// src/slices/prizeStructure/reducer.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PrizeTierConfig } from 'pages/PrizeStructure/types';
import { getDefaultRegion } from 'Components/constants/regions';
import {
  fetchPrizeTierConfigs,
  updatePrizeTierConfigs,
  createPrizeTierConfig,
  initializePrizeTierConfigs,
  deletePrizeTierConfig,
} from './thunk';

interface PrizeStructureState {
  region: string; // Miền đang chọn — mặc định lấy từ lms_cache_system_config, người dùng có thể đổi
  tiers: PrizeTierConfig[]; // draft đang chỉnh sửa tại chỗ trên bảng
  isDirty: boolean;
  loading: boolean;
  submitting: boolean;
  creating: boolean;
  initializing: boolean;
  deletingCode: string | null;
  error: string | null;
}

const initialState: PrizeStructureState = {
  region: getDefaultRegion(),
  tiers: [],
  isDirty: false,
  loading: false,
  submitting: false,
  creating: false,
  initializing: false,
  deletingCode: null,
  error: null,
};

const prizeStructureSlice = createSlice({
  name: 'PrizeStructure',
  initialState,
  reducers: {
    setRegion: (state, action: PayloadAction<string>) => {
      state.region = action.payload;
      state.tiers = [];
      state.isDirty = false;
    },
    // Sửa 1 field của 1 hạng giải ngay tại chỗ trên bảng (input inline — ui_co_cau_giai_thuong.md mục 4)
    setTierField: (
      state,
      action: PayloadAction<{ code: string; field: keyof PrizeTierConfig; value: any }>
    ) => {
      const tier = state.tiers.find((t) => t.code === action.payload.code) as any;
      if (tier) {
        tier[action.payload.field] = action.payload.value;
        state.isDirty = true;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPrizeTierConfigs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPrizeTierConfigs.fulfilled, (state, action) => {
        state.loading = false;
        state.tiers = action.payload || [];
        state.isDirty = false;
      })
      .addCase(fetchPrizeTierConfigs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(updatePrizeTierConfigs.pending, (state) => {
        state.submitting = true;
      })
      .addCase(updatePrizeTierConfigs.fulfilled, (state, action) => {
        state.submitting = false;
        state.tiers = action.payload || state.tiers;
        state.isDirty = false;
      })
      .addCase(updatePrizeTierConfigs.rejected, (state) => {
        state.submitting = false;
      })

      .addCase(createPrizeTierConfig.pending, (state) => {
        state.creating = true;
      })
      .addCase(createPrizeTierConfig.fulfilled, (state, action) => {
        state.creating = false;
        if (action.payload) {
          state.tiers = [...state.tiers, action.payload].sort((a, b) => a.displayOrder - b.displayOrder);
        }
      })
      .addCase(createPrizeTierConfig.rejected, (state) => {
        state.creating = false;
      })

      .addCase(initializePrizeTierConfigs.pending, (state) => {
        state.initializing = true;
      })
      .addCase(initializePrizeTierConfigs.fulfilled, (state, action) => {
        state.initializing = false;
        state.tiers = action.payload || [];
        state.isDirty = false;
      })
      .addCase(initializePrizeTierConfigs.rejected, (state) => {
        state.initializing = false;
      })

      .addCase(deletePrizeTierConfig.pending, (state, action) => {
        state.deletingCode = action.meta.arg.code;
      })
      .addCase(deletePrizeTierConfig.fulfilled, (state, action) => {
        state.deletingCode = null;
        state.tiers = state.tiers.filter((t) => t.code !== action.payload);
      })
      .addCase(deletePrizeTierConfig.rejected, (state) => {
        state.deletingCode = null;
      });
  },
});

export const { setRegion, setTierField } = prizeStructureSlice.actions;
export default prizeStructureSlice.reducer;
